import { useQueryClient } from '@tanstack/react-query';
import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ChatImg from 'src/assets/icons/chat-2.png';
import { ActionReceiveMessage, TopicDetailSendingStatus } from 'src/constants/topic.enum';
import urlPath from 'src/constants/urlPath';
import { TopicContext } from 'src/contexts/topic.context';
import { useChatFileService } from 'src/pages/v2/Topic/hook/useChatFileService';
import { useChatStore } from 'src/stores/chatStore';
import { IFilePath, ITopicDetail, ITopicMember, SignalMember, SignalTopic } from 'src/types/helpdesk/topic.type';

interface ReceiveMessageResponseType {
  action: ActionReceiveMessage;
  topicMessage: ITopicDetail;
}
interface Props {
  children?: React.ReactNode;
}

export default function ReceiveNotify({ children }: Props) {
  const { setPinTopicDetails, pinTopicDetails, connection } = useContext(TopicContext);
  const [signalMessage, setSignalMessage] = useState<ITopicDetail | undefined>(undefined);
  const queryClient = useQueryClient();
  const [signalTopic, setSignalTopic] = useState<SignalTopic | undefined>(undefined);
  const [signalMember, setSignalMember] = useState<SignalMember | undefined>(undefined);
  const [action, setAction] = useState<ActionReceiveMessage | undefined>(undefined);
  const { id } = useParams();
  const navigate = useNavigate();

  const { handleAddMessageFromSignR } = useChatFileService();

  // const groupMessage = (messageGroup: IGroupedTopicMessaged[], topicDetail: ITopicDetail) => {
  //   let groupedMessages: IGroupedTopicMessaged[] = [...(messageGroup || [])];
  //   groupedMessages.reverse();

  //   const existMessage = groupedMessages[groupedMessages.length - 1].topicMessageGroup.find(
  //     (x) => x.id === topicDetail?.id
  //   );

  //   if (!!existMessage) {
  //     groupedMessages.reverse();
  //     return groupedMessages;
  //   }

  //   const check = hasUnderOneHourAndSamePerson({
  //     previousDate: groupedMessages[groupedMessages.length - 1]?.createdDate,
  //     previousSenderCode: groupedMessages[groupedMessages.length - 1]?.senderCode,
  //     currentDate: topicDetail?.createdDate,
  //     currentSenderCode: topicDetail?.senderCode
  //   });

  //   if (check) {
  //     groupedMessages[groupedMessages.length - 1].topicMessageGroup.push({
  //       ...topicDetail,
  //       parentId: groupedMessages[groupedMessages.length - 1].id
  //     });
  //   } else {
  //     let convertData: IGroupedTopicMessaged;
  //     convertData = {
  //       id: topicDetail.id ?? '',
  //       topicId: topicDetail.topicId,
  //       createdDate: topicDetail.createdDate,
  //       senderBy: topicDetail.senderBy as FlatItem,
  //       senderCode: topicDetail.senderCode,
  //       createdByUser: topicDetail.createdByUser,
  //       content: topicDetail.content,
  //       tags: topicDetail.tags,
  //       gradientBackgroundId: topicDetail?.gradientBackgroundId,
  //       gradientBackground: topicDetail?.gradientBackground,
  //       topicMessageGroup: [{ ...topicDetail, parentId: topicDetail?.id }],
  //       lastModifiedDate: topicDetail.lastModifiedDate
  //     };
  //     groupedMessages.push(convertData);
  //   }

  //   groupedMessages.reverse();
  //   return groupedMessages;
  // };

  // Function kiểm tra message đã tồn tại trong store chưa
  const isMessageExistsInStore = useCallback((messageId: string, topicId: string): boolean => {
    const store = useChatStore.getState();
    const roomMessages = store.rooms[topicId]?.allMessages || [];

    // Kiểm tra trong tất cả message groups
    const exists = roomMessages.some((msg) => msg.id === messageId || msg.tempId === messageId);

    return exists;
  }, []);

  const handleNotificationWindow = useCallback(
    (topicDetail: ITopicDetail) => {
      if (Notification.permission === 'granted' || Notification.permission === 'default') {
        let notificationWindow: Notification;
        // If the user is on the same topic, do not show notification

        notificationWindow = new Notification('Tin nhắn mới', {
          body: `${topicDetail?.senderBy?.name}: ${topicDetail?.content}`,
          icon: ChatImg,
          dir: 'ltr'
        });

        notificationWindow.onclick = (event) => {
          event.preventDefault();
          if (topicDetail.topicId) {
            navigate(`${urlPath.topic.location}/${topicDetail.topicId}`);
          }
        };
      }
    },
    [navigate]
  );

  // Listen event from hub
  useEffect(() => {
    if (connection) {
      connection.on('ReceiveMessage', ({ action, topicMessage }: ReceiveMessageResponseType) => {
        setAction(action);
        setSignalMessage(topicMessage as ITopicDetail);
        console.log('ReceiveMessage', action, topicMessage);
      });
      connection.on('ReceiveMessageWhenPinning', (response: SignalTopic) => {
        console.log('ReceiveMessageWhenPinning', response);

        setSignalTopic(response);
      });
      connection.on('ReceiveMessageWhenAddMember', (response: SignalMember) => {
        setSignalMember(response);
      });
      connection.on('ReceiveMessageWhenHasEvent', ({ action, topicMessage }: ReceiveMessageResponseType) => {
        setAction(action);
        setSignalMessage(topicMessage as ITopicDetail);
      });
    }
  }, [connection, id]);

  const handleReceiveMessage = useCallback(
    (topicDetail: ITopicDetail) => {
      // Kiểm tra message đã tồn tại chưa
      if (isMessageExistsInStore(topicDetail.id as string, topicDetail.topicId)) {
        return; // Không thêm nữa
      }
      console.log('✅ ReceiveMessage:', topicDetail.id, 'in topic', topicDetail.topicId);

      if (topicDetail.topicId === id) {
        // THêm vào store
        const messageForStore = {
          ...topicDetail,
          topicId: topicDetail.topicId,
          sendingStatus: TopicDetailSendingStatus.Sent // Message từ SignalR đã được gửi thành công
        };

        try {
          handleAddMessageFromSignR(messageForStore);
        } catch (error) {
          console.error('❌ Error adding SignalR message to store:', error);
        }

        // add file
        if (topicDetail?.filePaths && topicDetail.filePaths.length > 0) {
          const store = useChatStore.getState();
          store.updateRoomPartial(topicDetail.topicId, {
            attachments: [
              ...(store.rooms[topicDetail.topicId]?.attachments || []),
              ...(topicDetail.filePaths as IFilePath[])
            ]
          });
        }

        const store = useChatStore.getState();
        store.updateRoomPartial(topicDetail.topicId, {
          lastestTopicDetail: topicDetail,
          lastActivity: topicDetail.createdDate
        });

        // update last activity
        queryClient.invalidateQueries({
          queryKey: ['readAllMessage', id]
        });
      } else {
        // Message cho topic khác - cũng update vào store
        const messageForStore = {
          ...topicDetail,
          topicId: topicDetail.topicId,
          sendingStatus: TopicDetailSendingStatus.Sent
        };

        try {
          handleAddMessageFromSignR(messageForStore);

          // Update room info cho topic khác
          const store = useChatStore.getState();
          store.updateRoomPartial(topicDetail.topicId, {
            lastestTopicDetail: topicDetail,
            lastActivity: topicDetail.createdDate,
            countUnReadMessages: {
              topicId: topicDetail.topicId,
              quantity: (store.rooms[topicDetail.topicId]?.countUnReadMessages?.quantity || 0) + 1
            }
          });

          console.log('✅ Other room updated in store:', topicDetail.topicId); // ← DEBUG
        } catch (error) {
          console.error('❌ Error adding SignalR message to other room:', error);
        }

        handleNotificationWindow(topicDetail);
      }
    },
    [id, queryClient, handleNotificationWindow, handleAddMessageFromSignR, isMessageExistsInStore]
  );

  // Handle when receive topic message
  useEffect(() => {
    if (signalMessage && action) {
      const topicDetail = signalMessage;
      handleReceiveMessage(topicDetail);
      setSignalMessage(undefined);
      setAction(undefined);
    }
  }, [action, handleReceiveMessage, signalMessage]);

  // Handle when receive message pin
  useEffect(() => {
    if (signalTopic) {
      switch (signalTopic.action) {
        case ActionReceiveMessage.Added: {
          const index = pinTopicDetails.findIndex((x) => x.id === signalTopic.topicMessage.id);
          const clonePinTopicDetails = [...pinTopicDetails];

          if (index !== -1) {
            const pinItem = clonePinTopicDetails.splice(index, 1)[0];
            setPinTopicDetails((prev) => [pinItem, ...prev]);
          } else if (pinTopicDetails.length >= 3) {
            clonePinTopicDetails.pop();
            clonePinTopicDetails.unshift(signalTopic.topicMessage);
            setPinTopicDetails(clonePinTopicDetails);

            setPinTopicDetails((prev) => [...prev, ...clonePinTopicDetails]);
          } else {
            clonePinTopicDetails.unshift(signalTopic.topicMessage);
            setPinTopicDetails(clonePinTopicDetails);
          }
          break;
        }
        case ActionReceiveMessage.Modified:
          {
            const clonePinTopicDetails = [...pinTopicDetails];

            const index = clonePinTopicDetails.findIndex((x) => x.id === signalTopic.topicMessage.id);
            const pinItem = clonePinTopicDetails.splice(index, 1)[0];
            clonePinTopicDetails.unshift(pinItem);
            setPinTopicDetails(clonePinTopicDetails);
          }
          break;
        case ActionReceiveMessage.Deleted:
          {
            const clonePinTopicDetails = [...pinTopicDetails];

            const newPinTopicDetails = clonePinTopicDetails.filter((item) => item.id !== signalTopic.topicMessage.id);
            setPinTopicDetails(newPinTopicDetails);
          }
          break;

        default:
          break;
      }

      setSignalTopic(undefined); // ← SỬA: Reset sau khi xử lý
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPinTopicDetails, signalTopic, pinTopicDetails]);

  // Handle when receive member change
  useEffect(() => {
    if (signalMember) {
      const store = useChatStore.getState();

      switch (signalMember.action) {
        case ActionReceiveMessage.Added:
          const currentRoomForAdd = store.rooms[signalMember.topicId] || {};

          store.updateRoomPartial(signalMember.topicId, {
            topicMembers: [...(currentRoomForAdd.topicMembers || []), signalMember?.topicMessage as ITopicMember]
          });

          break;

        case ActionReceiveMessage.Deleted:
          const currentRoomForDelete = store.rooms[signalMember.topicId] || {};

          store.updateRoomPartial(signalMember.topicId, {
            topicMembers: (currentRoomForDelete.topicMembers || []).filter(
              (member) => member?.id !== signalMember?.topicMessage?.id
            )
          });

          break;

        default:
          break;
      }

      setSignalMember(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalMember]);

  return <Fragment>{children}</Fragment>;
}
